import React, { useState } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { defaultRules } from "./PricingRules";
import { computeHpaOverride } from "../../utils/hpaLookup";

// ── Shared button style — every bulk-action button uses this base,
// so Set Pending / Set Reviewed / Bulk Sync / Bulk Generate / Export
// all look like one consistent family instead of mismatched styles.
const baseBtnStyle = {
  padding: "6px 14px",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  background: "#fff",
  color: "#374151",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.15s ease",
  whiteSpace: "nowrap",
};

const baseBtnHoverProps = {
  onMouseEnter: (e) => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.color = "#1d4ed8"; },
  onMouseLeave: (e) => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.color = "#374151"; },
};

function BulkButton({ onClick, disabled, children, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...baseBtnStyle,
        ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
        ...style,
      }}
      {...(!disabled ? baseBtnHoverProps : {})}
    >
      {children}
    </button>
  );
}

// All exportable antibody fields, with friendly labels — shown as a
// full checklist so the user can pick exactly which columns to include
// when exporting, instead of always getting a fixed hardcoded subset.
const EXPORT_FIELDS = [
  { key: "id",                            label: "ID" },
  { key: "item_name_number",              label: "Item Name/Number" },
  { key: "isotype",                       label: "Isotype" },
  { key: "antigen",                       label: "Antigen" },
  { key: "alternative_antigen_names",     label: "Alternative Antigen Names" },
  { key: "depositor_growth_medium",       label: "Depositor Growth Medium" },
  { key: "depositor_institution",         label: "Depositor Institution" },
  { key: "depositor_email",               label: "Depositor Email" },
  { key: "depositor_website",             label: "Depositor Website" },
  { key: "depositor_name_contributor",    label: "Depositor Name" },
  { key: "host_species",                  label: "Host Species" },
  { key: "antigen_species",               label: "Antigen Species" },
  { key: "positive_tested_species",       label: "Positive Tested Species" },
  { key: "alternate_name_clone_id",       label: "Alternate Name / Clone ID" },
  { key: "additional_information",        label: "Additional Information" },
  { key: "additional_characterization",   label: "Additional Characterization" },
  { key: "initial_pubmed_id",             label: "Initial PubMed ID" },
  { key: "depositor_notes",               label: "Depositor Notes" },
  { key: "antigen_molecular_weight",      label: "Antigen Molecular Weight" },
  { key: "gene",                          label: "Gene" },
  { key: "alternate_gene_names",          label: "Alternate Gene Names" },
  { key: "entrez_gene_id",                label: "Entrez Gene ID" },
  { key: "uniprot_id",                    label: "UniProt ID" },
  { key: "immunogen",                     label: "Immunogen" },
  { key: "immunogen_sequence",            label: "Immunogen Sequence" },
  { key: "clonality",                     label: "Clonality" },
  { key: "myeloma_strain",                label: "Myeloma Strain" },
  { key: "epitope_mapped",                label: "Epitope Mapped" },
  { key: "epitope_location_sequence",     label: "Epitope Location/Sequence" },
  { key: "epitope_map_pubmed_id",         label: "Epitope Map PubMed ID" },
  { key: "ihc_pubmed_id",                 label: "IHC PubMed ID" },
  { key: "if_pubmed_ids",                 label: "IF PubMed IDs" },
  { key: "wb_pubmed_id",                  label: "WB PubMed ID" },
  { key: "ip_pubmed_ids",                 label: "IP PubMed IDs" },
  { key: "chip_pubmed_ids",               label: "ChIP PubMed IDs" },
  { key: "elisa_pubmed_ids",              label: "ELISA PubMed IDs" },
  { key: "ffpe_pubmed_ids",               label: "FFPE PubMed IDs" },
  { key: "fb_pubmed_ids",                 label: "Function Blocking PubMed IDs" },
  { key: "microarray_pubmed_ids",         label: "Microarray PubMed IDs" },
  { key: "trap_pubmed_ids",               label: "TRAP PubMed IDs" },
  { key: "facs_pubmed_ids",               label: "FACS PubMed IDs" },
  { key: "gel_supershift_pubmed_ids",     label: "Gel Supershift PubMed IDs" },
  { key: "other_pubmed_ids",              label: "Other PubMed IDs" },
  { key: "applications",                  label: "Applications" },
  { key: "collections",                   label: "Collections" },
  { key: "antibody_registry_id",          label: "Antibody Registry ID" },
  { key: "status",                        label: "Status" },
  { key: "date",                          label: "Date Submitted" },
];

// Default selection — the columns the export modals start pre-checked
// with, matching what used to be hardcoded (ID/Item Name/Isotype/
// Depositor/Email/Status/Date), so the common case is still one click.
const DEFAULT_EXPORT_KEYS = new Set([
  "id", "item_name_number", "isotype", "depositor_name_contributor",
  "depositor_email", "status", "date",
]);

function getExportValue(product, key) {
  if (key === "date") return product.date ? new Date(product.date).toLocaleString() : "";
  const v = product[key];
  if (Array.isArray(v)) return v.join(", ");
  return v ?? "";
}

export default function BulkActions({ selectedProducts, allProducts, setProducts, clearSelection, pricingRules }) {

  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [showSuffixModal, setShowSuffixModal] = useState(false);
  const [selectedSuffixes, setSelectedSuffixes] = useState(["-s", "-f", "-g"]);

  // Export field-selection modal
  const [exportModalType, setExportModalType] = useState(null); // "excel" | "csv" | null
  const [exportFields, setExportFields] = useState(new Set(DEFAULT_EXPORT_KEYS));

  const selectedProductObjects = allProducts.filter((p) =>
    selectedProducts.includes(p.id)
  );

  const checkSelection = () => {
    if (selectedProducts.length === 0) {
      toast.warning("No products selected!");
      return false;
    }
    return true;
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (!checkSelection()) return;

    try {
      // Update each selected product in DB
      await Promise.all(selectedProductObjects.map(p =>
        fetch("https://dshb-deposits.its.uiowa.edu/api/update-status", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            submission_id: p.submission_id || p.id,
            status: newStatus
          })
        })
      ));

      const now = new Date();
      const updated = allProducts.map((p) => {
        if (selectedProducts.includes(p.id)) {
          return { ...p, status: newStatus, lastUpdated: now };
        }
        return p;
      });

      setProducts(updated);
      clearSelection();
      toast.success(`Status updated to "${newStatus}" for ${selectedProducts.length} products`);
    } catch (err) {
      toast.error("Bulk status update failed: " + err.message);
    }
  };

  // ✅ Clean a single product for serialization
  const cleanDeposit = (product, hpaOverride) => ({
    ...(hpaOverride ? { human_protein_atlas_override: hpaOverride } : {}),
    submission_id: String(product.submission_id || product.id || ""),
    item_name_number: String(product.item_name_number || ""),
    isotype: String(product.isotype || ""),
    antigen: String(product.antigen || ""),
    depositor_growth_medium: String(product.depositor_growth_medium || ""),
    depositor_institution: String(product.depositor_institution || ""),
    depositor_email: String(product.depositor_email || ""),
    depositor_website: String(product.depositor_website || ""),
    depositor_name_contributor: String(product.depositor_name_contributor || ""),
    host_species: String(product.host_species || ""),
    antigen_species: String(product.antigen_species || ""),
    positive_tested_species: String(product.positive_tested_species || ""),
    alternate_name_clone_id: String(product.alternate_name_clone_id || ""),
    additional_information: String(product.additional_information || ""),
    additional_characterization: String(product.additional_characterization || ""),
    initial_pubmed_id: String(product.initial_pubmed_id || ""),
    depositor_notes: String(product.depositor_notes || ""),
    antigen_molecular_weight: String(product.antigen_molecular_weight || ""),
    gene: String(product.gene || ""),
    alternate_gene_names: String(product.alternate_gene_names || ""),
    entrez_gene_id: String(product.entrez_gene_id || ""),
    uniprot_id: String(product.uniprot_id || ""),
    immunogen: String(product.immunogen || ""),
    immunogen_sequence: String(product.immunogen_sequence || ""),
    clonality: String(product.clonality || ""),
    myeloma_strain: String(product.myeloma_strain || ""),
    epitope_mapped: String(product.epitope_mapped || ""),
    epitope_location_sequence: String(product.epitope_location_sequence || ""),
    epitope_map_pubmed_id: String(product.epitope_map_pubmed_id || ""),
    ihc_pubmed_id: String(product.ihc_pubmed_id || ""),
    if_pubmed_ids: String(product.if_pubmed_ids || ""),
    wb_pubmed_id: String(product.wb_pubmed_id || ""),
    ip_pubmed_ids: String(product.ip_pubmed_ids || ""),
    chip_pubmed_ids: String(product.chip_pubmed_ids || ""),
    elisa_pubmed_ids: String(product.elisa_pubmed_ids || ""),
    ffpe_pubmed_ids: String(product.ffpe_pubmed_ids || ""),
    fb_pubmed_ids: String(product.fb_pubmed_ids || ""),
    microarray_pubmed_ids: String(product.microarray_pubmed_ids || ""),
    trap_pubmed_ids: String(product.trap_pubmed_ids || ""),
    facs_pubmed_ids: String(product.facs_pubmed_ids || ""),
    gel_supershift_pubmed_ids: String(product.gel_supershift_pubmed_ids || ""),
    other_pubmed_ids: String(product.other_pubmed_ids || ""),
    applications: Array.isArray(product.applications)
      ? product.applications.join(", ")
      : String(product.applications || ""),
    collections: String(product.collections || ""),
    antibody_registry_id: String(product.antibody_registry_id || ""),
    status: String(product.status || ""),
    id: String(product.id || product.submission_id || ""),
    // CORRECTED 2026-10-20: this allowlist was missing these 5 fields
    // entirely -- confirmed live, every bulk-generated file was
    // silently writing generator.js's own fallback defaults instead of
    // the real values: "Date Deposited" came out blank (no default),
    // and "Display in Web Store"/"allow sales to for-profits"/"Allow
    // Hybridoma Distribution" all silently became "No" and "sequencing
    // allowed" silently became "Yes" regardless of the item's real
    // value, since generator.js's toYesNo() falls back when the field
    // is simply missing from the object, not just when it's explicitly
    // false. The single-item Generate flow in ProductDetails.js never
    // had this bug -- it copies every key off `product` instead of
    // using a hand-maintained allowlist like this one.
    date_deposited: String(product.date_deposited || ""),
    display_in_web_store: String(product.display_in_web_store || ""),
    allow_sales_for_profit: String(product.allow_sales_for_profit || ""),
    allow_hybridoma_distribution: String(product.allow_hybridoma_distribution || ""),
    sequencing_allowed: String(product.sequencing_allowed || ""),
  });

  // ✅ Clean pricing rules
  const getCleanPricingRules = () => {
    const rules = pricingRules || defaultRules;
    return rules.map(rule => ({
      suffix: String(rule.suffix || ""),
      label: String(rule.label || ""),
      productForm: String(rule.productForm || ""),
      allowProfit: Boolean(rule.allowProfit),
      allowHybridoma: Boolean(rule.allowHybridoma || false),
      npPrices: Array.isArray(rule.npPrices) ? rule.npPrices.map(t => ({
        price: Number(t?.price) || 0,
        qty: Number(t?.qty) || 0,
        level: String(t?.level || "")
      })) : [],
      fpPrices: Array.isArray(rule.fpPrices) ? rule.fpPrices.map(t => ({
        price: Number(t?.price) || 0,
        qty: Number(t?.qty) || 0,
        level: String(t?.level || "")
      })) : []
    }));
  };

  const handleBulkGenerate = async (suffixes) => {
    if (!checkSelection()) return;
    setBulkGenerating(true);
    setBulkProgress({ current: 0, total: selectedProductObjects.length });
    setShowSuffixModal(false);

    try {
      // Same reason as the single-item Generate flow in ProductDetails.js:
      // the backend can't reach proteinatlas.org (firewall), but this
      // browser can, and HPA's API allows cross-origin requests - so the
      // lookup happens here, per selected product, before sending the
      // batch off, same as the single-item flow already does.
      const hpaOverrides = await Promise.all(
        selectedProductObjects.map(p => computeHpaOverride(p))
      );
      const cleanDeposits = selectedProductObjects.map((p, i) => cleanDeposit(p, hpaOverrides[i]));
      const safePricingRules = getCleanPricingRules();

      setBulkProgress({ current: selectedProductObjects.length, total: selectedProductObjects.length });

      const response = await fetch("https://dshb-deposits.its.uiowa.edu/api/bulk-generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deposits: cleanDeposits,
          pricingRules: safePricingRules,
          selectedSuffixes: suffixes || selectedSuffixes
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Bulk generate failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bulk_generated_${selectedProductObjects.length}_antibodies.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Commerce category CSV — same as the single-item Generate flow,
      // just sending the whole batch as one array. generateCommerceCategoryCSV
      // already handles an array of deposits (one combined CSV covering
      // every selected item), so no backend change was needed for this.
      try {
        const ccResponse = await fetch("https://dshb-deposits.its.uiowa.edu/api/generate-commerce-category", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deposit: cleanDeposits }),
        });
        // Firefox (and Safari) commonly block a second auto-triggered
        // download fired immediately after the first with no gap --
        // confirmed live: both files download in Chrome, only the
        // .xlsx ever lands in Firefox. Same fix as the single-item
        // Generate flow in ProductDetails.js: a short delay before the
        // second click.
        await new Promise((resolve) => setTimeout(resolve, 600));
        if (ccResponse.ok) {
          const ccBlob = await ccResponse.blob();
          const ccUrl = window.URL.createObjectURL(ccBlob);
          const ccA = document.createElement("a");
          ccA.href = ccUrl;
          ccA.download = `bulk_commerce_categories_${selectedProductObjects.length}_antibodies.csv`;
          document.body.appendChild(ccA);
          ccA.click();
          ccA.remove();
          window.URL.revokeObjectURL(ccUrl);
        } else {
          toast.warn("CSV generated, but the commerce category CSV failed.");
        }
      } catch {
        toast.warn("CSV generated, but the commerce category CSV failed.");
      }

      toast.success(`✅ Generated CSV for ${selectedProductObjects.length} antibodies!`);
      clearSelection();

    } catch (err) {
      console.error("Bulk generate error:", err);
      toast.error("Bulk generate failed: " + err.message);
    } finally {
      setBulkGenerating(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  };

  const handleBulkSync = () => {
    if (!checkSelection()) return;
    toast.info("NetSuite sync not yet configured. Generate Excel first and import manually.");
  };

  const openExportModal = (type) => {
    if (!checkSelection()) return;
    setExportModalType(type);
  };

  const toggleExportField = (key) => {
    setExportFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const runExport = () => {
    if (exportFields.size === 0) {
      toast.error("Select at least one field to export.");
      return;
    }
    const chosenFields = EXPORT_FIELDS.filter(f => exportFields.has(f.key));
    const data = selectedProductObjects.map((p) => {
      const row = {};
      chosenFields.forEach(f => { row[f.label] = getExportValue(p, f.key); });
      return row;
    });

    if (exportModalType === "excel") {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SelectedProducts");
      XLSX.writeFile(wb, "selected_products.xlsx");
      toast.success("Excel exported successfully");
    } else {
      const ws = XLSX.utils.json_to_sheet(data);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "selected_products.csv");
      toast.success("CSV exported successfully");
    }
    setExportModalType(null);
  };

  return (
    <>
      {selectedProducts.length > 0 && (
        <div className="bulk-actions" style={{
          display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center",
          padding: "10px 15px", marginBottom: "10px",
          background: "#fff", borderRadius: "8px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        }}>

          {/* Progress indicator */}
          {bulkGenerating && (
            <div style={{
              padding: "6px 12px", background: "#eff6ff", border: "1px solid #93c5fd",
              borderRadius: "6px", fontSize: "12px", color: "#1e40af", fontWeight: 600
            }}>
              ⏳ Generating {bulkProgress.current}/{bulkProgress.total}...
            </div>
          )}

          <BulkButton onClick={() => handleBulkStatusChange("Pending")}>Set Pending</BulkButton>
          <BulkButton onClick={() => handleBulkStatusChange("Reviewed")}>Set Reviewed</BulkButton>
          <BulkButton onClick={() => handleBulkStatusChange("Approved")}>Set Approved</BulkButton>
          <BulkButton onClick={handleBulkSync}>Bulk Sync</BulkButton>

          <BulkButton
            onClick={() => setShowSuffixModal(true)}
            disabled={bulkGenerating}
          >
            {bulkGenerating ? "Generating…" : `Bulk Generate (${selectedProducts.length})`}
          </BulkButton>

          <BulkButton onClick={() => openExportModal("excel")}>Export Excel</BulkButton>
          <BulkButton onClick={() => openExportModal("csv")}>Export CSV</BulkButton>

          <span style={{ flex: 1 }} />

          <BulkButton
            onClick={clearSelection}
            style={{ borderColor: "#fca5a5", color: "#dc2626" }}
          >
            Clear Selection
          </BulkButton>
        </div>
      )}

      {/* Suffix Selection Modal */}
      {showSuffixModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 1000
        }}>
          <div style={{
            background: "white", borderRadius: "10px", padding: "24px",
            width: "380px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <h3 style={{ margin: "0 0 8px 0", fontSize: "15px", color: "#1e293b" }}>
              Bulk Generate — Select Child Types
            </h3>
            <p style={{ fontSize: "12px", color: "#64748b", margin: "0 0 16px 0" }}>
              Generating for <strong>{selectedProducts.length}</strong> antibodies.
              Choose which product forms to include:
            </p>

            {[
              { suffix: "-s", label: "Supernatant (-s)" },
              { suffix: "-f", label: "Frozen Cells (-f)" },
              { suffix: "-g", label: "Growing Cells (-g)" },
              { suffix: "-x", label: "Purified Antibody (-x)" },
              { suffix: "-p", label: "Polyclonal Sera (-p)" }
            ].map(({ suffix, label }) => (
              <label key={suffix} style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 10px", marginBottom: "6px",
                background: selectedSuffixes.includes(suffix) ? "#eff6ff" : "#f8fafc",
                border: `1px solid ${selectedSuffixes.includes(suffix) ? "#3b82f6" : "#e2e8f0"}`,
                borderRadius: "5px", cursor: "pointer", fontSize: "13px", fontWeight: "500"
              }}>
                <input
                  type="checkbox"
                  checked={selectedSuffixes.includes(suffix)}
                  onChange={() => setSelectedSuffixes(prev =>
                    prev.includes(suffix)
                      ? prev.filter(s => s !== suffix)
                      : [...prev, suffix]
                  )}
                />
                {label}
              </label>
            ))}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
              <button
                onClick={() => setShowSuffixModal(false)}
                style={{
                  padding: "8px 14px", border: "1px solid #cbd5e1",
                  borderRadius: "6px", background: "#fff", color: "#374151",
                  cursor: "pointer", fontSize: "12px", fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleBulkGenerate(selectedSuffixes)}
                disabled={selectedSuffixes.length === 0}
                style={{
                  padding: "8px 16px", border: "1px solid #cbd5e1",
                  background: selectedSuffixes.length === 0 ? "#f3f4f6" : "#1a2c5b",
                  color: selectedSuffixes.length === 0 ? "#9ca3af" : "#fff",
                  borderRadius: "6px",
                  cursor: selectedSuffixes.length === 0 ? "not-allowed" : "pointer",
                  fontSize: "12px", fontWeight: 600,
                }}
              >
                Generate {selectedProducts.length} × {selectedSuffixes.length + 1} rows
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Field Selection Modal */}
      {exportModalType && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 1000
        }}>
          <div style={{
            background: "white", borderRadius: "10px", width: "460px", maxHeight: "82vh",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column",
          }}>
            <div style={{ padding: "20px 24px 0" }}>
              <h3 style={{ margin: "0 0 4px 0", fontSize: "15px", color: "#1e293b" }}>
                Export {exportModalType === "excel" ? "Excel" : "CSV"} — Choose Fields
              </h3>
              <p style={{ fontSize: "12px", color: "#64748b", margin: "0 0 12px 0" }}>
                Exporting <strong>{selectedProducts.length}</strong> antibodies. Pick which fields to include:
              </p>
              <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                <button
                  onClick={() => setExportFields(new Set(EXPORT_FIELDS.map(f => f.key)))}
                  style={{ background: "none", border: "none", color: "#2563eb", fontSize: "12px", cursor: "pointer", padding: 0, fontWeight: 600 }}
                >
                  Select All
                </button>
                <button
                  onClick={() => setExportFields(new Set())}
                  style={{ background: "none", border: "none", color: "#6b7280", fontSize: "12px", cursor: "pointer", padding: 0, fontWeight: 600 }}
                >
                  Clear All
                </button>
              </div>
            </div>

            <div style={{
              overflowY: "auto", padding: "0 24px", flex: 1,
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px",
            }}>
              {EXPORT_FIELDS.map(f => (
                <label key={f.key} style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  padding: "6px 8px", borderRadius: "5px", cursor: "pointer",
                  fontSize: "12px", color: "#374151",
                  background: exportFields.has(f.key) ? "#eff6ff" : "transparent",
                }}>
                  <input
                    type="checkbox"
                    checked={exportFields.has(f.key)}
                    onChange={() => toggleExportField(f.key)}
                  />
                  {f.label}
                </label>
              ))}
            </div>

            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              gap: "8px", padding: "16px 24px", borderTop: "1px solid #e5e7eb", marginTop: "8px",
            }}>
              <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                {exportFields.size} field{exportFields.size !== 1 ? "s" : ""} selected
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => setExportModalType(null)}
                  style={{
                    padding: "8px 14px", border: "1px solid #cbd5e1",
                    borderRadius: "6px", background: "#fff", color: "#374151",
                    cursor: "pointer", fontSize: "12px", fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={runExport}
                  disabled={exportFields.size === 0}
                  style={{
                    padding: "8px 16px", border: "1px solid #cbd5e1",
                    background: exportFields.size === 0 ? "#f3f4f6" : "#1a2c5b",
                    color: exportFields.size === 0 ? "#9ca3af" : "#fff",
                    borderRadius: "6px",
                    cursor: exportFields.size === 0 ? "not-allowed" : "pointer",
                    fontSize: "12px", fontWeight: 600,
                  }}
                >
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}